import { Button, Container, Table } from "react-bootstrap"
import React, { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import useLocalStorageState from "use-local-storage-state"
import { GetRequest, PostRequest } from "../Utils/hooks/ApiHandler"
import { toast } from "sonner"
import { BsArrowLeft } from "react-icons/bs"

// Permission hierarchy configuration
const PERMISSION_HIERARCHY = {
    view: [],
    create: ['view'],
    edit: ['view', 'create'],
    delete: ['view', 'create', 'edit']
}

// Modules with only view permission (no create/edit/delete)
const VIEW_ONLY_MODULES = ['dashboard']

// Modules with cascading permission logic
const CASCADING_MODULES = [
    'reports', 'project', 'calender', 'workflow',
    'payment', 'campaigning', 'customizations', 'integration', 'setting'
]

// Modules with sub-features that depend on main feature
const HIERARCHICAL_MODULES = [
    'contact', 'company', 'leads', 'deals', 'quotation',
    'sales order', 'invoice', 'recurring invoice', 'purchase order'
]

export const RolePermission = () => {
    const [customer, setCustomer] = useLocalStorageState("customer")
    const navigate = useNavigate()
    const location = useLocation()
    const token = customer?.token?.authorization
    const rf = customer?.token?.authentication
    const [isFetching, setIsFetching] = useState(false)
    const [roleModuleData, setRoleModuleData] = useState(null)
    const data = location?.state || null

    const headers = {
        Authentication: rf,
        Authorization: token,
    }

    const extras = {
        customer,
        setCustomer,
        rf,
        navigate,
    }

    useEffect(() => {
        if (data === null) {
            navigate("/settings/roleManagement")
            toast.warning("Please select a Role first!")
        }
    }, [data])

    // Apply permission hierarchy (if edit is checked, view and create must be checked)
    const applyPermissionHierarchy = (permissions, field, value) => {
        const result = { ...permissions }

        if (value === true) {
            // When enabling a permission, enable all prerequisites
            PERMISSION_HIERARCHY[field]?.forEach(prereq => {
                result[prereq] = true
            })
            result[field] = true
        } else {
            // When disabling a permission, disable all dependent permissions
            result[field] = false
            Object.entries(PERMISSION_HIERARCHY).forEach(([perm, prereqs]) => {
                if (prereqs.includes(field)) {
                    result[perm] = false
                }
            })
        }

        return result
    }

    // Check if module is the main feature (first feature in the list)
    const isMainFeature = (module, feature) => {
        return module.features[0]?.feature_id === feature.feature_id
    }

    // Check if feature is import/export
    const isActionFeature = (featureName) => {
        const name = featureName.toLowerCase()
        return name === 'import' || name === 'export'
    }

    // Get main feature for a module
    const getMainFeature = (module) => {
        return module.features[0]
    }

    const handleManagePermission = (module_id, feature_id, field, value) => {
        setRoleModuleData((prev) => ({
            ...prev,
            modules: prev.modules.map((module) => {
                if (module.module_id !== module_id) return module

                const moduleName = module.module_name.toLowerCase()
                const targetFeature = module.features.find(f => f.feature_id === feature_id)

                if (!targetFeature) return module

                // Handle view-only modules (Dashboard)
                if (VIEW_ONLY_MODULES.includes(moduleName)) {
                    return {
                        ...module,
                        features: module.features.map(f => {
                            if (f.feature_id === feature_id && field === 'view') {
                                return {
                                    ...f,
                                    view: value,
                                    create: false,
                                    edit: false,
                                    delete: false,
                                }
                            }
                            return f
                        })
                    }
                }

                // Handle Reports module - check if corresponding module has view permission
                if (moduleName === 'reports' && value === true) {
                    const correspondingModuleName = targetFeature.feature_name
                        .toLowerCase()
                        .split(" ")
                        .join("_")

                    const correspondingModule = prev.modules.find(
                        m => m.module_name.toLowerCase().split(" ").join("_") === correspondingModuleName
                    )

                    if (correspondingModule && !correspondingModule.features[0]?.view) {
                        toast.warning(
                            `Please give permission to ${correspondingModule.module_name}'s view first!`
                        )
                        return module
                    }
                }

                // Handle cascading modules (Reports, Project, etc.)
                if (CASCADING_MODULES.includes(moduleName)) {
                    return {
                        ...module,
                        features: module.features.map(f => {
                            if (f.feature_id === feature_id) {
                                return {
                                    ...f,
                                    ...applyPermissionHierarchy(f, field, value)
                                }
                            }
                            return f
                        })
                    }
                }

                // Handle hierarchical modules (Contact, Company, etc.)
                if (HIERARCHICAL_MODULES.includes(moduleName)) {
                    const mainFeature = getMainFeature(module)
                    const isMain = isMainFeature(module, targetFeature)
                    const isAction = isActionFeature(targetFeature.feature_name)

                    let updatedFeatures = [...module.features]

                    if (isAction) {
                        // Handle import/export
                        updatedFeatures = updatedFeatures.map(f => {
                            if (f.feature_id === feature_id) {
                                const updated = { ...f, [field]: value }

                                // Import needs view and edit on main feature
                                if (field === 'import' && value === true) {
                                    if (!mainFeature.view || !mainFeature.edit) {
                                        updatedFeatures = updatedFeatures.map(mf =>
                                            mf.feature_id === mainFeature.feature_id
                                                ? { ...mf, view: true, edit: true }
                                                : mf
                                        )
                                    }
                                }

                                // Export needs view on main feature
                                if (field === 'export' && value === true) {
                                    if (!mainFeature.view) {
                                        updatedFeatures = updatedFeatures.map(mf =>
                                            mf.feature_id === mainFeature.feature_id
                                                ? { ...mf, view: true }
                                                : mf
                                        )
                                    }
                                }

                                return updated
                            }
                            return f
                        })
                    } else if (isMain) {
                        // Handle main feature permission changes
                        updatedFeatures = updatedFeatures.map(f => {
                            if (f.feature_id === feature_id) {
                                return {
                                    ...f,
                                    ...applyPermissionHierarchy(f, field, value)
                                }
                            }
                            return f
                        })

                        // If main view is disabled, disable all sub-features
                        if (field === 'view' && value === false) {
                            updatedFeatures = updatedFeatures.map(f => ({
                                ...f,
                                view: false,
                                create: false,
                                edit: false,
                                delete: false,
                                export: isActionFeature(f.feature_name) ? false : f.export
                            }))
                        }

                        // If disabling edit/create on main, disable import
                        if ((field === 'edit' || field === 'create') && value === false) {
                            updatedFeatures = updatedFeatures.map(f =>
                                isActionFeature(f.feature_name) && f.feature_name.toLowerCase() === 'import'
                                    ? { ...f, import: false }
                                    : f
                            )
                        }

                    } else {
                        // Handle sub-feature permission changes
                        updatedFeatures = updatedFeatures.map(f => {
                            if (f.feature_id === feature_id) {
                                return {
                                    ...f,
                                    ...applyPermissionHierarchy(f, field, value)
                                }
                            }
                            return f
                        })

                        // If enabling any permission on sub-feature, enable main view
                        if (value === true) {
                            updatedFeatures = updatedFeatures.map(f =>
                                f.feature_id === mainFeature.feature_id
                                    ? { ...f, view: true }
                                    : f
                            )
                        }
                    }

                    return {
                        ...module,
                        features: updatedFeatures
                    }
                }

                // Default behavior for other modules
                return {
                    ...module,
                    features: module.features.map(f =>
                        f.feature_id === feature_id
                            ? { ...f, [field]: value }
                            : f
                    )
                }
            }),
        }))
    }

    const handleSelectAllForModule = (module_id, checked) => {
        setRoleModuleData((prev) => ({
            ...prev,
            modules: prev.modules.map((module) => {
                if (module.module_id !== module_id) return module

                const moduleName = module.module_name.toLowerCase()

                // For view-only modules, only set view permission
                if (VIEW_ONLY_MODULES.includes(moduleName)) {
                    return {
                        ...module,
                        features: module.features.map((f) => ({
                            ...f,
                            view: checked,
                            create: false,
                            edit: false,
                            delete: false,
                        })),
                    }
                }

                return {
                    ...module,
                    features: module.features.map((f) => {
                        const isAction = isActionFeature(f.feature_name)
                        if (isAction) {
                            return {
                                ...f,
                                [f.feature_name.toLowerCase()]: checked
                            }
                        }
                        return {
                            ...f,
                            view: checked,
                            create: checked,
                            edit: checked,
                            delete: checked,
                        }
                    }),
                }
            }),
        }))
    }

    const handleFinalSubmit = async () => {
        const permission_data = roleModuleData["modules"]
            .flatMap(module =>
                module.features.map(f => {
                    const featureName = f.feature_name?.toLowerCase() || ""
                    const isImport = featureName === "import"
                    const isExport = featureName === "export"

                    let isAction = 0
                    if ((isImport && f.import) || (isExport && f.export)) {
                        isAction = 1
                    }

                    return {
                        module_id: module.module_id,
                        feature_id: f.feature_id,
                        view: f.view ? 1 : 0,
                        create: f.create ? 1 : 0,
                        edit: f.edit ? 1 : 0,
                        delete: f.delete ? 1 : 0,
                        is_action: isAction,
                    }
                })
            )

        try {
            const payload = {
                customer_id: customer.customer_details?.customer_id,
                role_id: data.role_id,
                permissions: permission_data,
            }

            const res = await PostRequest(
                "plan/insertModulePermission",
                payload,
                false,
                { headers },
                extras
            )

            if (res.status === "fail" || res.success === false) {
                toast.error(res.message)
                return
            }

            toast.success(res.message)
            navigate('/settings/roleManagement')
        } catch (error) {
            console.error(error)
        }
    }

    const normalizeFeatures = (modules) => {
        return modules.map((module) => ({
            ...module,
            features: module.features.map((f) => {
                const featureName = f.feature_name.toLowerCase()
                const isAction = featureName === "import" || featureName === "export"

                return isAction ? {
                    ...f,
                    view: Boolean(Number(f.view)),
                    edit: Boolean(Number(f.edit)),
                    create: Boolean(Number(f.create)),
                    delete: Boolean(Number(f.delete)),
                    [featureName]: Boolean(Number(f.is_action)),
                } : {
                    ...f,
                    view: Boolean(Number(f.view)),
                    edit: Boolean(Number(f.edit)),
                    create: Boolean(Number(f.create)),
                    delete: Boolean(Number(f.delete)),
                }
            }),
        }))
    }

    useEffect(() => {
        (async () => {
            setIsFetching(true)

            try {
                const res = await GetRequest(
                    `/plan/fetchCustomerPlan_modules/${data?.role_id}/${customer.customer_details?.customer_id}`,
                    { headers },
                    extras
                )

                if (res.status === "fail" || res.success === false) {
                    setIsFetching(false)
                    return
                }

                const normalized = normalizeFeatures(res.data[0].modules)
                setRoleModuleData({
                    ...res.data[0],
                    modules: normalized,
                })
            } catch (error) {
                console.error(error)
            } finally {
                setIsFetching(false)
            }
        })()
    }, [])

    return (
        <>
            <div className="w-100 p-3">
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex flex-column text-start">
                        <h5 className="fw-semibold mb-1">Permission Settings</h5>
                        <p className="text-muted small">
                            Control access by assigning permissions to roles.
                        </p>
                    </div>
                    <Button
                        className="btn btn-default-outline"
                        onClick={() => navigate(-1)}
                    >
                        <BsArrowLeft className="me-1" /> Back
                    </Button>
                </div>
                <hr
                    className="mt-3 mb-2"
                    style={{ borderTop: "0.5px solid rgb(111, 116, 121)", opacity: "0.3" }}
                />
                {isFetching ? (
                    <Container
                        fluid
                        style={{
                            fontFamily: "'Poppins', sans-serif",
                            backgroundColor: "white",
                            height: "calc(100vh - 15rem)",
                            padding: 0,
                            animation: "cardEntry 1s ease-out forwards",
                        }}
                        className="mt-3 p-0"
                    >
                        <div className="mb-0" style={{ height: "100%" }}>
                            <Table className="listStyleForContact container-table">
                                <thead style={{ zIndex: 101 }}>
                                    <tr style={{ textAlign: "start" }}>
                                        <th style={{ textAlign: "left" }}>Module</th>
                                        <th style={{ textAlign: "center" }}>View</th>
                                        <th style={{ textAlign: "center" }}>Create</th>
                                        <th style={{ textAlign: "center" }}>Edit</th>
                                        <th style={{ textAlign: "center" }}>Delete</th>
                                        <th style={{ textAlign: "center" }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody style={{ textAlign: "start" }}>
                                    {Array.from({ length: 15 }).map((_, rowIndex) => (
                                        <tr
                                            key={rowIndex}
                                            style={{ marginTop: "10px", marginBottom: "10px" }}
                                        >
                                            {Array.from({ length: 6 }).map((_, colIndex) => (
                                                <td key={colIndex} style={{ textAlign: "center" }}>
                                                    <div className="d-flex">
                                                        {colIndex !== 0 && (
                                                            <div style={{ paddingLeft: "5.7rem" }}></div>
                                                        )}
                                                        <div
                                                            style={{
                                                                textAlign: "center",
                                                                height: "20px",
                                                                backgroundColor: "#e0e0e0",
                                                                borderRadius: "4px",
                                                                animation: "pulse 1.5s ease-in-out infinite",
                                                                width: colIndex === 0 ? "5rem" : "2rem",
                                                            }}
                                                        ></div>
                                                    </div>
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </Container>
                ) : (
                    <Container
                        fluid
                        style={{
                            fontFamily: "'Poppins', sans-serif",
                            height: "calc(100vh - 15rem)",
                            padding: 0,
                            animation: "cardEntry 1s ease-out forwards",
                        }}
                        className="mt-3 p-0"
                    >
                        <div className="mb-0" style={{ height: "100%" }}>
                            <Table className="listStyleForContact container-table">
                                <thead style={{ zIndex: 101 }}>
                                    <tr style={{ textAlign: "start" }}>
                                        <th style={{ textAlign: "left" }}>Module</th>
                                        <th style={{ textAlign: "center" }}>View</th>
                                        <th style={{ textAlign: "center" }}>Create</th>
                                        <th style={{ textAlign: "center" }}>Edit</th>
                                        <th style={{ textAlign: "center" }}>Delete</th>
                                        <th style={{ textAlign: "center" }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {roleModuleData?.modules?.map((module) => (
                                        <React.Fragment key={module.module_id}>
                                            <tr>
                                                <td
                                                    style={{
                                                        textAlign: "left",
                                                        fontWeight: "bold",
                                                        display: "flex",
                                                        width: "220px",
                                                        alignItems: "center",
                                                    }}
                                                    className="fontstyle"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={module.features.every(
                                                            (f) => {
                                                                const isAction = isActionFeature(f.feature_name)
                                                                const isViewOnly = VIEW_ONLY_MODULES.includes(module.module_name.toLowerCase())

                                                                if (isViewOnly) {
                                                                    return f.view
                                                                }
                                                                if (isAction) {
                                                                    return f[f.feature_name.toLowerCase()]
                                                                }
                                                                return f.view && f.create && f.edit && f.delete
                                                            }
                                                        )}
                                                        className="select-all-input"
                                                        onChange={(e) =>
                                                            handleSelectAllForModule(
                                                                module.module_id,
                                                                e.target.checked
                                                            )
                                                        }
                                                        style={{
                                                            accentColor: "#7a3a5e",
                                                            marginRight: "0.5rem",
                                                        }}
                                                    />
                                                    {module.module_name}
                                                </td>
                                                <td className="fontstyle"></td>
                                                <td className="fontstyle"></td>
                                                <td className="fontstyle"></td>
                                                <td className="fontstyle"></td>
                                                <td className="fontstyle"></td>
                                            </tr>
                                            {module.features.map((feature) => {
                                                const isAction = isActionFeature(feature.feature_name)
                                                const actionField = feature.feature_name.toLowerCase()
                                                const isViewOnly = VIEW_ONLY_MODULES.includes(module.module_name.toLowerCase())

                                                return (
                                                    <tr key={feature.feature_id}>
                                                        <td style={{ textAlign: "left" }}>
                                                            <span style={{ paddingLeft: "0.4rem" }}></span>
                                                            {feature.feature_name}
                                                        </td>
                                                        {isViewOnly ? (
                                                            <>
                                                                <td style={{ textAlign: "center" }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={!!feature.view}
                                                                        onChange={(e) =>
                                                                            handleManagePermission(
                                                                                module.module_id,
                                                                                feature.feature_id,
                                                                                "view",
                                                                                e.target.checked
                                                                            )
                                                                        }
                                                                        style={{ accentColor: "#7a3a5e" }}
                                                                    />
                                                                </td>
                                                                <td style={{ textAlign: "center" }}>-</td>
                                                                <td style={{ textAlign: "center" }}>-</td>
                                                                <td style={{ textAlign: "center" }}>-</td>
                                                                <td style={{ textAlign: "center" }}>-</td>
                                                            </>
                                                        ) : isAction ? (
                                                            <>
                                                                <td style={{ textAlign: "center" }}>-</td>
                                                                <td style={{ textAlign: "center" }}>-</td>
                                                                <td style={{ textAlign: "center" }}>-</td>
                                                                <td style={{ textAlign: "center" }}>-</td>
                                                                <td style={{ textAlign: "center" }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={!!feature[actionField]}
                                                                        onChange={(e) =>
                                                                            handleManagePermission(
                                                                                module.module_id,
                                                                                feature.feature_id,
                                                                                actionField,
                                                                                e.target.checked
                                                                            )
                                                                        }
                                                                        style={{ accentColor: "#7a3a5e" }}
                                                                    />
                                                                </td>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <td style={{ textAlign: "center" }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={!!feature.view}
                                                                        onChange={(e) =>
                                                                            handleManagePermission(
                                                                                module.module_id,
                                                                                feature.feature_id,
                                                                                "view",
                                                                                e.target.checked
                                                                            )
                                                                        }
                                                                        style={{ accentColor: "#7a3a5e" }}
                                                                    />
                                                                </td>
                                                                <td style={{ textAlign: "center" }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={!!feature.create}
                                                                        onChange={(e) =>
                                                                            handleManagePermission(
                                                                                module.module_id,
                                                                                feature.feature_id,
                                                                                "create",
                                                                                e.target.checked
                                                                            )
                                                                        }
                                                                        style={{ accentColor: "#7a3a5e" }}
                                                                    />
                                                                </td>
                                                                <td style={{ textAlign: "center" }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={!!feature.edit}
                                                                        onChange={(e) =>
                                                                            handleManagePermission(
                                                                                module.module_id,
                                                                                feature.feature_id,
                                                                                "edit",
                                                                                e.target.checked
                                                                            )
                                                                        }
                                                                        style={{ accentColor: "#7a3a5e" }}
                                                                    />
                                                                </td>
                                                                <td style={{ textAlign: "center" }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={!!feature.delete}
                                                                        onChange={(e) =>
                                                                            handleManagePermission(
                                                                                module.module_id,
                                                                                feature.feature_id,
                                                                                "delete",
                                                                                e.target.checked
                                                                            )
                                                                        }
                                                                        style={{ accentColor: "#7a3a5e" }}
                                                                    />
                                                                </td>
                                                                <td style={{ textAlign: "center" }}>-</td>
                                                            </>
                                                        )}
                                                    </tr>
                                                )
                                            })}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </Table>
                            <button
                                type="submit"
                                className="btn btn-default float-end mb-4 mt-1 px-3"
                                onClick={handleFinalSubmit}
                            >
                                Save
                            </button>
                        </div>
                    </Container>
                )}
            </div>
            <style jsx>{`
                .select-all-input {
                    width: 10% !important;
                    padding: 8px 10px;
                    border-radius: 6px;
                    border: 1px solid #ccc;
                }
                .fontstyle {
                    font-size: 1rem !important;
                    background-color: #eff3f5 !important;
                }
            `}</style>
        </>
    )
}